import { inject } from '@angular/core';
import { withCallStatus } from '@ngrx-traits/signals';
import {
  patchState,
  signalStoreFeature,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { AuthApiService } from '../auth-api/auth-api.service';
import { User } from '@seatix/shared-domain-auth';

export function withProfileUpdate() {
  return signalStoreFeature(
    withState<{ profileUpdatedData: Partial<User> | null }>({
      profileUpdatedData: null,
    }),
    withCallStatus({
      prop: 'profileUpdate',
      errorType: 'string',
    }),
    withMethods((store) => {
      const authApiService = inject(AuthApiService);
      const updateProfile = rxMethod<Partial<User>>(
        pipe(
          switchMap((user) => {
            // Marca `loading` al despachar, no solo al aterrizar. El call
            // status es compartido y nada lo devuelve a `init`, así que sin
            // esto un consumidor que espere el desenlace lee el resultado de
            // la llamada anterior: un `loaded` viejo se lee como éxito antes
            // de que el PATCH responda, y un `error` viejo bloquea el
            // reintento. Además limpia el error previo, porque el status es
            // un único campo de unión.
            store.setProfileUpdateLoading();

            return authApiService.patchUser(user).pipe(
              tap((response) => {
                store.setProfileUpdateLoaded();
                // La respuesta del PATCH tiene la misma forma que `GET /me`
                // (`UserService::updateMe` devuelve `make($user)->me()`), así
                // que trae el `phone_verified_at` que el servidor acaba de
                // dejar: guardar un número distinto lo pone en null. Adoptarlo
                // es lo que mantiene honesto al cliente — sin esto se queda con
                // la copia previa, el chip dice «Verificado» sobre un número sin
                // probar, `openIfUnverified()` no encuentra nada que verificar y
                // el banner no aparece hasta recargar la página.
                const { first_name, last_name, phone, phone_verified_at } =
                  response.data;
                patchState(store, {
                  profileUpdatedData: {
                    first_name,
                    last_name,
                    phone,
                    phone_verified_at,
                  },
                });
              }),
              catchError(() => {
                const errorMessage = 'Ocurrió un error al actualizar el perfil';
                store.setProfileUpdateError(errorMessage);
                return EMPTY;
              }),
            );
          }),
        ),
      );

      // `withCallStatus` no trae reset y el `resetCallStatus()` de
      // `withAuthentication` solo cubre login/register/socialLogin, así que sin
      // esto un `error` de un guardado fallido se queda en el status —
      // compartido, de sesión— hasta el siguiente guardado exitoso. Quien
      // abandone la edición debe dejarlo como lo encontró.
      function resetProfileUpdateState() {
        patchState(store, { profileUpdateCallStatus: 'init' });
      }

      return {
        updateProfile,
        resetProfileUpdateState,
      };
    }),
  );
}
